import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Lock, MapPin, Camera } from "lucide-react";

export default function KioskKit() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [error, setError] = useState("");
  const url = `${window.location.origin}/timeclock/${companyId}`;

  useEffect(() => {
    api.get(`/companies/${companyId}/kiosk-kit`).then((r) => setKit(r.data)).catch(() => setError("You don't have access to this kit."));
  }, [companyId]);

  if (error) return <div className="min-h-screen grid place-items-center text-muted-foreground">{error}</div>;
  if (!kit) return <div className="min-h-screen grid place-items-center text-muted-foreground">Preparing kiosk kit…</div>;
  const { company, employees, kiosk } = kit;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="kiosk-kit-page">
      <div className="print:hidden sticky top-0 z-10 bg-slate-950 text-white px-6 py-3 flex items-center gap-3">
        <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate(-1)} data-testid="kit-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="font-display font-semibold">Kiosk Kit — {company.name}</div>
        <Button className="ml-auto" onClick={() => window.print()} data-testid="kit-print-btn"><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
      </div>

      <div className="max-w-[820px] mx-auto p-8 print:p-0">
        {/* Page 1: QR poster */}
        <section className="break-after-page min-h-[95vh] flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-4 mb-8">
            {company.logo_url ? <img src={company.logo_url} alt={company.name} className="h-20 object-contain" /> : <span className="text-6xl">{company.logo}</span>}
            <div className="text-left">
              <div className="text-4xl font-bold font-display" style={{ color: company.color }}>{company.name}</div>
              <div className="text-slate-500 text-lg">Employee Time Clock</div>
            </div>
          </div>
          <div className="p-6 rounded-3xl border-4" style={{ borderColor: company.color }}>
            <QRCodeSVG data-testid="kit-qr" value={url} size={320} level="M" includeMargin={false} />
          </div>
          <p className="mt-6 text-2xl font-semibold">Scan to open the time clock</p>
          <p className="mt-2 text-slate-500 font-mono-num break-all text-sm">{url}</p>
          <ol className="mt-8 text-left text-slate-700 space-y-2 max-w-md">
            <li>1. Open the time clock link on the shared tablet or your phone.</li>
            <li>2. Enter your personal 4-digit PIN on the keypad.</li>
            <li>3. Tap <b>Clock In / Out</b>. Your name and time appear on screen.</li>
          </ol>
          <div className="mt-8 flex gap-3 text-xs text-slate-500">
            {kiosk.has_passcode && <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Device lock enabled</span>}
            {kiosk.geofence_enabled && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location check ({kiosk.radius_m}m)</span>}
            {kiosk.photo_enabled && <span className="flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Photo on punch</span>}
          </div>
          <div className="mt-10 flex items-center gap-2 text-slate-400 text-xs">Powered by <img src="/rightpay-logo.png" alt="RightPay" className="h-5" /></div>
        </section>

        {/* Page 2: PIN sheet */}
        <section className="pt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold font-display">Employee PIN Sheet — {company.name}</h2>
            <span className="text-xs px-2 py-1 rounded bg-rose-100 text-rose-700 font-semibold">CONFIDENTIAL — HR ONLY</span>
          </div>
          <p className="text-sm text-slate-500 mb-4">Cut along the lines and hand each employee their own PIN slip. {employees.length} active employees.</p>
          <div className="grid grid-cols-2 gap-3" data-testid="kit-pin-grid">
            {employees.map((e) => (
              <div key={e.id} data-testid={`kit-pin-${e.id}`} className="border border-dashed border-slate-300 rounded-lg p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{e.name}</div>
                  <div className="text-xs text-slate-500 truncate">{e.job_title} · {e.department}</div>
                  <div className="text-[10px] text-slate-400 mt-1 truncate">{url}</div>
                </div>
                <div className="text-right pl-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Clock PIN</div>
                  <div className="font-mono-num text-2xl font-bold tracking-widest" style={{ color: company.color }}>{e.pin}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
